export default class ConditionalTexts {
    constructor() {
        window.classConditionalTexts = this;

        this.wrapper = document.querySelector('#wrapper');

        if (!this.wrapper) {
            return;
        }

        this.conditionalGroups = null;

        this.questionConditionsWereSet = false;
        this.questionMatch = false;

        this.parametersConditionsWereSet = false;
        this.parameterMatch = false;

        this.formStepConditionsWereSet = false;
        this.formStepMatch = false;

        this.setupConditionalGroups();

        // Check if any conditional texts should be shown already.
        // This method is also called by:
        // - the quiz script when the last question in a quiz is answered
        // - the form script when the next / previous form step is shown
        this.checkConditionalTexts();
    }

    /**
     * Structure all conditional elements into groups.
     *
     * A 'conditional group' consists of:
     * - the name of the element in the admin
     * - the default text element
     * - the conditional text element(s)
     */
    setupConditionalGroups() {
        // Get all conditional elements, including the default elements.
        // Transform it to an array so the `reduce()` method can be used.
        const conditionalKeys = Array.from(this.wrapper.querySelectorAll('[data-conditional-key]'));

        // Reduce the array of conditional text elements into a map structure where they are grouped as key-value pairs.
        // - key: {String} the name of the element in the admin
        // - value: {Object}
        //      - defaultElement: {HTMLElement} the default element that will always be shown, unless a condition is met, in which case the default element will be hidden
        //      - conditionalElements: {Object} an object with all conditional variations of the default element
        //          - key: {String} the condition, which is an object in string format
        //          - value: {HTMLElement} the element, which is shown when the condition is met
        //
        this.conditionalGroups = conditionalKeys.reduce((map, element) => {
            // Check if the current element should be part of an existing group
            let conditionalGroupObject = map.get(element.dataset.conditionalKey);

            if (conditionalGroupObject) {
                // The group exists, add current element as a conditional element
                conditionalGroupObject.conditionalElements[element.dataset.conditionals] = element;

                // Update current group in the map
                map.set(element.dataset.conditionalKey, conditionalGroupObject);
            } else {
                // The group does not exist yet, create it with the current element as its default element,
                // since the default element of the group is found first
                conditionalGroupObject = {
                    defaultElement: element,
                    conditionalElements: {},
                }

                // Store group in the map
                map.set(element.dataset.conditionalKey, conditionalGroupObject);
            }

            return map;
        }, new Map());
    }

    /**
     * Check if any conditional elements should be shown, based on matches for question conditions and parameter conditions.
     */
    checkConditionalTexts() {
        // First reset all conditional elements to their original displayed state.
        this.resetConditionalElements();

        // Loop through the conditional groups stored in the map, by retrieving its value.
        // The value is an object containing the default element and its conditional elements.
        for (const conditionalGroup of this.conditionalGroups.values()) {
            // Store the default element
            const defaultElement = conditionalGroup.defaultElement;

            // Loop through the conditional elements of the group and get the key-value pairs.
            for (let [conditionalElementKey, conditionalElementValue] of Object.entries(conditionalGroup.conditionalElements)) {
                // The key is an object of conditions formatted as a string, so first convert it to a usable object.
                conditionalElementKey = JSON.parse(`${conditionalElementKey}`);

                // Match question conditions.
                this.matchConditionalQuestions(conditionalElementKey);

                // Match parameter conditions.
                this.matchConditionalParameters(conditionalElementKey);

                // Match form step conditions.
                this.matchConditionalFormSteps(conditionalElementKey);

                // Toggle elements based on matches.
                this.toggleConditionalElements(defaultElement, conditionalElementValue);

                // Reset match states.
                this.resetMatches();
            }
        }
    }

    /**
     * Match conditional questions with the answers given by the user.
     *
     * The conditional questions are formatted as '[<number>_<value>]', so they are split on '_'
     * to match the conditional question numbers and answers with the answers of the user.
     *
     * The answers from the user are being stored by the quiz script.
     *
     * A conditional question can accept one or multiple answers.
     * When any of those answers is given by the user, it's a match.
     *
     * @param {Object} conditionalElementKey
     */
    matchConditionalQuestions(conditionalElementKey) {
        // Create a map to store each conditional question number and answers in key-value format.
        // The key will be the conditional question number and the value an array of its answer(s).
        const conditionalQuestionAnswerCombinations = new Map();

        // Get the question conditions of the current conditional element.
        const conditionalQuestions = conditionalElementKey.questions;

        // Loop through each conditional question.
        for (const conditionalQuestion of conditionalQuestions) {
            // Split the conditional question number and conditional answer.
            const conditionalQuestionNumber = conditionalQuestion.split('_')[0];
            const conditionalQuestionValue = conditionalQuestion.split('_')[1];

            // Check if the current conditional question number has not been stored yet.
            const storedConditionalQuestion = conditionalQuestionAnswerCombinations.get(conditionalQuestionNumber);

            if (storedConditionalQuestion) {
                // The conditional question number exists, update its conditional answers with the current conditional answer.
                conditionalQuestionAnswerCombinations.set(conditionalQuestionNumber, [...storedConditionalQuestion, conditionalQuestionValue]);
            } else {
                // The conditional question number does not exist yet, store it with the current conditional answer
                conditionalQuestionAnswerCombinations.set(conditionalQuestionNumber, [conditionalQuestionValue]);
            }
        }

        // This element has question conditions that have been stored, start matching.
        if (conditionalQuestionAnswerCombinations.size) {
            // Update state.
            this.questionConditionsWereSet = true;

            // For each stored conditional question number, get its array of stored conditional answer(s),
            // and then check if the question that the user answered fits the conditional answer(s).
            // A conditional question may also accept multiple answers, so check if the answer of the user is included in that array.
            for (const [formattedConditionalQuestionNumber, formattedConditionalQuestionAnswers] of conditionalQuestionAnswerCombinations) {
                if (formattedConditionalQuestionAnswers.includes(classQuiz.storedAnswers[formattedConditionalQuestionNumber])) {
                    this.questionMatch = true;
                } else {
                    this.questionMatch = false;
                    return;
                }
            }
        }
    }

    /**
     * Match conditional parameters with the parameters present in the url.
     *
     * The conditional parameters are formatted as '[<name>=<value>]', so they are split on '='
     * to match the conditional parameter name and values with those present in the url.
     *
     * A conditional parameter can either be the parameter itself, or a full parameter with a value.
     *
     * In the admin, multiple parameter values with the same name can also be specified.
     * When a parameter in the url contains any of those values, it's a match.
     *
     * @param {Object} conditionalElementKey
     */
    matchConditionalParameters(conditionalElementKey) {
        // Create a map to store each conditional parameter names and its value in key-value format.
        // The key will be the conditional parameter name and the value an array of strings.
        // When no value was set, the value in this array will be `undefined`.
        const conditionalParameterCombinations = new Map();

        // Get the parameter conditions of the current conditional element,
        // which consists of objects with 'parameter' as key and the conditional parameter as its value.
        const conditionalParameters = conditionalElementKey.url_parameters;

        // Loop through each conditional parameter.
        for (const conditionalParameter of conditionalParameters) {
            // Split the conditional parameter name and (optional) conditional parameter value.
            const conditionalParameterName = conditionalParameter.parameter.split('=')[0];
            const conditionalParameterValue = conditionalParameter.parameter.split('=')[1];

            // Check if the current conditional parameter name has not been stored yet.
            const storedConditionalParameter = conditionalParameterCombinations.get(conditionalParameterName);

            if (storedConditionalParameter) {
                // The conditional parameter name exists, update its conditional values with the current conditional value.
                conditionalParameterCombinations.set(conditionalParameterName, [...storedConditionalParameter, conditionalParameterValue]);
            } else {
                // The conditional parameter name does not exist yet, store it with the current conditional value
                conditionalParameterCombinations.set(conditionalParameterName, [conditionalParameterValue]);
            }
        }

        // This element has parameter conditions that have been stored, start matching.
        if (conditionalParameterCombinations.size) {
            // Update state.
            this.parametersConditionsWereSet = true;

            // Get all parameters from the url.
            const queryString = window.location.search;
            const urlParameters = new URLSearchParams(queryString);

            // Create an object to store all url parameters.
            const storedParameters = {};

            // Loop through the url parameters and store it as key-value pairs.
            for (const [key, value] of urlParameters.entries()) {
                storedParameters[key] = value;
            }

            // For each stored conditional parameter, get its array of stored conditional value(s),
            // and then check if this parameter is present in the url.
            // A conditional parameter may also accept multiple values, so check if the value of the matching url parameter is included in that array.
            for (const [formattedConditionalParameterName, formattedConditionalParameterValues] of conditionalParameterCombinations) {
                // Parameter name and value match
                if (storedParameters[formattedConditionalParameterName] && formattedConditionalParameterValues.includes(storedParameters[formattedConditionalParameterName])) {
                    this.parameterMatch = true;

                    // Parameter matches where no value was specified
                } else if (storedParameters[formattedConditionalParameterName] && formattedConditionalParameterValues[0] === undefined) {
                    this.parameterMatch = true;

                    // One of the conditional parameters was not present in the url; stop checking and return.
                } else {
                    this.parameterMatch = false;
                    return;
                }
            }
        }
    }

    /**
     * Match conditional form steps with the active step of the form.
     *
     * The conditional form steps are formatted as '[<value>]'.
     *
     * @param {Object} conditionalElementKey
     */
    matchConditionalFormSteps(conditionalElementKey) {
        const conditionalFormSteps = conditionalElementKey.form_steps;

        // The `data-form-step` value on the body is set by the form script which is empty on page load,
        // so in that case default to 1
        const activeFormStep = parseInt(document.querySelector('body').dataset.formStep) || 1;

        if (conditionalFormSteps.length) {
            this.formStepConditionsWereSet = true;
        }

        for (const conditionalFormStep of conditionalFormSteps) {
            if (conditionalFormStep === activeFormStep) {
                this.formStepMatch = true;
            }
        }
    }

    /**
     * Read the states of booleans for matches, and based on that show the specified conditional element and hide its default element.
     * First a match for a combination of all three conditional categories is done.
     * Then for a combination of two.
     * And lastly for a single category only.
     *
     * @param {HTMLElement} defaultElement
     * @param {HTMLElement} conditionalElementValue
     */
    toggleConditionalElements(defaultElement, conditionalElementValue) {
        // Might need a refactor
        if (
            // Questions + parameters + form steps were all set and all had a match.
            (
                this.questionConditionsWereSet && this.questionMatch
                && this.parametersConditionsWereSet && this.parameterMatch
                && this.formStepConditionsWereSet && this.formStepMatch
            )
            // Questions + parameters were set and both had a match
            || (
                this.questionConditionsWereSet && this.questionMatch
                && this.parametersConditionsWereSet && this.parameterMatch
            )
            // Questions + form steps were set and both had a match
            || (
                this.questionConditionsWereSet && this.questionMatch
                && this.formStepConditionsWereSet && this.formStepMatch
            )
            // Parameters + form steps were set and both had a match
            || (
                this.parametersConditionsWereSet && this.parameterMatch
                && this.formStepConditionsWereSet && this.formStepMatch
            )
            // Only questions were set and had a match
            || (
                this.questionConditionsWereSet && this.questionMatch
                && !this.parametersConditionsWereSet
                && !this.formStepConditionsWereSet
            )
            // Only parameters were set and had a match
            || (
                this.parametersConditionsWereSet && this.parameterMatch
                && !this.questionConditionsWereSet
                && !this.formStepConditionsWereSet
            )
            // Only form steps were set and had a match
            || (
                this.formStepConditionsWereSet && this.formStepMatch
                && !this.questionConditionsWereSet
                && !this.parametersConditionsWereSet
            )
        ) {
            // Show the related conditional element and hide the default text.
            conditionalElementValue.classList.remove('conditional');
            defaultElement.classList.add('conditional--default-hidden');
        }
    }

    /**
     * Reset the state of booleans so that they can be used again in their initial state.
     */
    resetMatches() {
        this.questionConditionsWereSet = false;
        this.questionMatch = false;

        this.parametersConditionsWereSet = false;
        this.parameterMatch = false;

        this.formStepConditionsWereSet = false;
        this.formStepMatch = false;
    }

    /**
     * Hide all conditional elements and show their default texts.
     */
    resetConditionalElements() {
        for (const conditionalGroup of this.conditionalGroups.values()) {
            const defaultElement = conditionalGroup.defaultElement;

            defaultElement.classList.remove('conditional--default-hidden');

            for (const conditionalElement of Object.values(conditionalGroup.conditionalElements)) {
                conditionalElement.classList.add('conditional');
            }
        }
    }
}

